import { Box, CircularProgress, Grid, TextField, InputAdornment, IconButton } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ErrorMessage } from "../../../utils/commonUtils";
import { SUCCESS } from "../../../utils/constants";
import { AuthorizationActions } from "../../api/RequestUtil";
import AlertService from "../../common/Alert/alertService";
import SelectPlanCard from "../../onBoarding/SelectPlanCard";

const SelectPlan = ({ selectedPlanId, setSelectedPlanId, onValidatedCodeChange }) => {
  const { isRiskFreeTier } = useSelector((state) => state.user);

  const [loader, setLoader] = useState(false);
  const [allPlan, setAllPlan] = useState([]);
  const { error, success } = AlertService();

  // Access code state
  const [promoCode, setPromoCode] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);
  const [unlockedPlan, setUnlockedPlan] = useState(null);

  const handleSelectPlan = (id) => {
    setSelectedPlanId(id);
  };

  const fetchAllPlan = () => {
    setLoader(true);
    AuthorizationActions.onBoardingGetAllPlan()
      .then(({ data }) => {
        if (data.status === SUCCESS) {
          setAllPlan(data.data);
        }
      })
      .catch(({ data }) => error(ErrorMessage(data)))
      .finally(() => setLoader(false));
  };

  // Validate access code
  const handleValidateCode = async () => {
    if (!promoCode.trim()) {
      error("Please enter an access code");
      return;
    }

    setValidatingCode(true);
    try {
      const { data } = await AuthorizationActions.validatePlanCode({ code: promoCode });

      if (data.status === SUCCESS) {
        const { codeId, plan } = data.data;
        setUnlockedPlan(plan);
        success(data.message || `Successfully unlocked ${plan.name}!`);
        setPromoCode(""); // Clear input after success

        // Notify parent component of validated code
        if (onValidatedCodeChange) {
          onValidatedCodeChange({ codeId, plan });
        }
      } else {
        error(data.message || "Invalid access code");
      }
    } catch (err) {
      error(ErrorMessage(err) || "Failed to validate access code");
    } finally {
      setValidatingCode(false);
    }
  };

  useEffect(() => {
    fetchAllPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loader) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="65vh">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Risk Free Trial plan ID
  const RISK_FREE_TRIAL_PLAN_ID = "9abe8d41-14ba-4df0-aad7-9777c9ef7b48";
  // Filter out Risk Free Trial plan robustly
  const updatedPlans = isRiskFreeTier ? allPlan : allPlan?.filter((plan) => plan.id !== RISK_FREE_TRIAL_PLAN_ID);

  // Combine regular plans with unlocked plan at the FRONT of the list (like onboarding)
  const plansToDisplay = unlockedPlan ? [unlockedPlan, ...updatedPlans] : updatedPlans;

  return (
    <>
      {/* Access Code Input */}
      <Box sx={{ mb: 3, mt: 2 }}>
        <TextField
          fullWidth
          label="Have a special access code?"
          placeholder="Enter code"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
          disabled={validatingCode || !!unlockedPlan}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              handleValidateCode();
            }
          }}
          InputLabelProps={{
            sx: {
              top: "50%",
              transform: "translateY(-50%)",
              paddingLeft: "14px",
              "&.MuiInputLabel-shrink": {
                top: 0,
                transform: "translate(14px, -9px) scale(0.75)",
                paddingLeft: 0,
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleValidateCode}
                  disabled={validatingCode || !!unlockedPlan || !promoCode.trim()}
                  edge="end"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "grey.300",
                      color: "rgba(0, 0, 0, 0.26) !important",
                    },
                  }}
                >
                  {validatingCode ? <CircularProgress size={20} /> : <LockOpenIcon sx={{ color: "inherit" }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Plans Grid */}
      <Grid
        container
        spacing={2}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
        }}
      >
        {plansToDisplay?.map((plan) => (
          <Grid item xs={12} sm={6} md={4} lg={4} key={plan.id} sx={{ display: "flex" }}>
            <SelectPlanCard
              data={plan}
              isSelected={plan.id === selectedPlanId}
              onClick={() => handleSelectPlan(plan.id)}
              isUnlocked={unlockedPlan && plan.id === unlockedPlan.id}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};

SelectPlan.propTypes = {
  selectedPlanId: PropTypes.string,
  setSelectedPlanId: PropTypes.func,
  onValidatedCodeChange: PropTypes.func,
};

export default SelectPlan;
